import { EditYourFoodRequest } from "@/clients/models/EditYourFoodRequest";
import { Meal } from "@/clients/models/Meal";
import { MealsService } from "@/clients/services/MealsService";
import { QueryKeys } from "@/constants/query-keys";
import {
  planDetailMealQueryKey,
  setPlanDetailMealData,
} from "@/features/meal-plan/hooks/use-meal-detail-data";
import { MealPlan } from "@/pages/plans/types/meal-plan.type";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export interface EditYourFoodParams {
  mealId: string;
  requestBody: EditYourFoodRequest;
}

export const useEditYourFood = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isError, error } = useMutation({
    mutationKey: [QueryKeys.YOUR_FOOD],
    mutationFn: async ({ mealId, requestBody }: EditYourFoodParams) => {
      const user = guardGetUserFromLocalStorage();
      const { data } = await MealsService.editYourFoodApiV1MealsYourFoodMealIdPut(
        mealId,
        user.id,
        requestBody,
      );
      return data;
    },
    onSuccess: (responseMeal, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.YOUR_FOOD] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.MY_FOODS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.PLAN] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SCALED_SERVINGS, variables.mealId],
      });

      const currentDetailMeal = queryClient.getQueryData<MealPlan | null>(planDetailMealQueryKey);
      if (!currentDetailMeal?.meal?.id || currentDetailMeal.meal.id !== variables.mealId) {
        return;
      }

      const nextMeal: Meal = responseMeal
        ? responseMeal
        : {
            ...currentDetailMeal.meal,
            name: variables.requestBody.recipe_name,
            ingredients: variables.requestBody.ingredients,
            instructions: variables.requestBody.instructions ?? [],
            nutritionalInfo: {
              ...currentDetailMeal.meal.nutritionalInfo,
              perPortion: {
                ...currentDetailMeal.meal.nutritionalInfo?.perPortion,
                size: String(variables.requestBody.portion_size),
              },
            },
          };

      setPlanDetailMealData(queryClient, {
        ...currentDetailMeal,
        meal: nextMeal,
      });
    },
  });

  return {
    editYourFood: mutate,
    editYourFoodAsync: mutateAsync,
    isEditing: isPending,
    isError,
    error,
  };
};
